'use client'

import type { ApplySuccessPageProps } from '@app/p/apply-success/[token]/[step]/page'
import { LiveAIAgent } from '@components/app/ApplyPackagePage/LiveAIAgent.client'
import { Icon } from '@components/primitives/Icon'
import ModalOverlay from '@components/primitives/Modal/ModalOverlay'
import { Text } from '@components/primitives/Text'
import { faMemoCircleCheck } from '@fortawesome/pro-regular-svg-icons/faMemoCircleCheck'
import { faMemoCircleInfo } from '@fortawesome/pro-regular-svg-icons/faMemoCircleInfo'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons/faCheckCircle'
import { faFileInvoice } from '@fortawesome/pro-solid-svg-icons/faFileInvoice'
import { faSpinner } from '@fortawesome/pro-solid-svg-icons/faSpinner'
import { faXmark } from '@fortawesome/pro-solid-svg-icons/faXmark'
import { defaultSampleRate } from '@lib/retell/consts'
import type { RetellEventTranscript } from '@lib/retell/types'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import unknownToString from '@lib/types/unknownToString'
import { getFirstName } from '@modules/users/helpers/getUserFirstName'
import { useRouter } from 'next/navigation'
import { useCallback, useEffect, useRef, useState } from 'react'
import { RetellWebClient } from 'retell-client-js-sdk'
import { getApplySuccessRouteToRedirectTo } from '../getApplySuccessRouteToRedirectTo'
import { PatientNavigatorSkipModal } from './PatientNavigatorSkipModal'

export function VerificationAgentCall({
  accessToken,
  allowPatientCallToSite,
  canPatientContactSite,
  consentForEmr,
  consentToShareYourStory,
  hasPatientNavigatorAgentCallCompleted,
  idToken,
  isFullyQualified,
  isQualifiedApply,
  mostQualifiedTrialApplicationIdToken,
  patientName,
  showPatientNavigatorAgentStep,
  showScreeningVisitQualificationStep,
  whyAreYouApplying,
}: Pick<
  ApplySuccessPageProps,
  | 'allowPatientCallToSite'
  | 'canPatientContactSite'
  | 'consentForEmr'
  | 'consentToShareYourStory'
  | 'hasPatientNavigatorAgentCallCompleted'
  | 'idToken'
  | 'isFullyQualified'
  | 'isQualifiedApply'
  | 'mostQualifiedTrialApplicationIdToken'
  | 'patientName'
  | 'patientUserAgent'
  | 'showPatientNavigatorAgentStep'
  | 'showScreeningVisitQualificationStep'
  | 'whyAreYouApplying'
> & {
  accessToken: string | null
}) {
  const router = useRouter()
  const [skipModalOpen, setSkipModalOpen] = useState(false)
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder>()
  const [mostRecentTranscript, setMostRecentTranscript] = useState('')
  const audioCtx = useRef<AudioContext>()
  const retellWebClientRef = useRef<RetellWebClient>()
  const callStartedAt = useRef<Date>()
  const hasSetupRetell = useRef<boolean>(false)

  const advanceToNextStep = useCallback(() => {
    router.push(
      getApplySuccessRouteToRedirectTo('patient-navigator-agent', {
        allowPatientCallToSite,
        canPatientContactSite,
        consentForEmr,
        consentToShareYourStory,
        hasPatientNavigatorAgentCallCompleted,
        idToken,
        isFullyQualified,
        isQualifiedApply,
        showPatientNavigatorAgentStep,
        showScreeningVisitQualificationStep,
        whyAreYouApplying,
      }),
    )
  }, [router, idToken])

  const setupRetellWebClient = useCallback(() => {
    // @ts-ignore
    audioCtx.current = new AudioContext({ sinkId: { type: 'none' } })
    const streamDestination = audioCtx.current.createMediaStreamDestination()
    const { stream } = streamDestination
    const mediaRecorder = new MediaRecorder(stream)
    mediaRecorder.start()
    setMediaRecorder(mediaRecorder)
    retellWebClientRef.current = new RetellWebClient()
    retellWebClientRef.current.on('call_started', () => {
      trackEvent(TrackingEvents.PATIENT_NAVIGATOR_AGENT_CALL_STARTED, {
        mostQualifiedTrialApplicationIdToken,
        trialApplicationIdToken: idToken,
        val: accessToken ?? undefined,
      })
      callStartedAt.current = new Date()
    })

    retellWebClientRef.current.on('call_ended', () => {
      const callDurationInSeconds = callStartedAt.current
        ? Math.round(
            (new Date().getTime() - callStartedAt.current.getTime()) / 1000,
          )
        : 0
      trackEvent(TrackingEvents.PATIENT_NAVIGATOR_AGENT_CALL_ENDED, {
        durationInSeconds: callDurationInSeconds,
        mostQualifiedTrialApplicationIdToken,
        trialApplicationIdToken: idToken,
        val: accessToken ?? undefined,
      })
      advanceToNextStep()
    })

    retellWebClientRef.current.on('agent_start_talking', () => {
      // consoleLogger.info('agent_start_talking')
    })

    retellWebClientRef.current.on('agent_stop_talking', () => {
      // consoleLogger.info('agent_stop_talking')
    })

    retellWebClientRef.current.on('audio', (audio: Float32Array) => {
      if (audioCtx.current) {
        const source = audioCtx.current.createBufferSource()
        source.connect(streamDestination)
        const buffer = audioCtx.current.createBuffer(
          1,
          audio.length,
          audioCtx.current.sampleRate,
        )
        const channelData = buffer.getChannelData(0)
        channelData.set(audio)
        source.buffer = buffer
        source.start()
      }
    })

    retellWebClientRef.current.on('update', (update: RetellEventTranscript) => {
      const transcript = update.transcript[update.transcript.length - 1]
      if (transcript && transcript.role === 'agent') {
        setMostRecentTranscript(transcript.content)
      }
    })

    retellWebClientRef.current.on('error', (error) => {
      trackEvent(TrackingEvents.PATIENT_NAVIGATOR_AGENT_CALL_ERRORED, {
        mostQualifiedTrialApplicationIdToken,
        trialApplicationIdToken: idToken,
        type: unknownToString(error),
        val: accessToken ?? undefined,
      })
      retellWebClientRef.current?.stopCall()
    })
  }, [accessToken, idToken, mostQualifiedTrialApplicationIdToken])

  useEffect(() => {
    if (hasSetupRetell.current) return

    setupRetellWebClient()
    if (accessToken && retellWebClientRef.current) {
      hasSetupRetell.current = true
      retellWebClientRef.current.startCall({
        accessToken,
        emitRawAudioSamples: true,
        sampleRate: defaultSampleRate,
      })
    }
  }, [accessToken])

  const endCall = useCallback(() => {
    retellWebClientRef.current?.stopCall()
  }, [])

  return (
    <ModalOverlay
      className='bg-gradient-to-b from-white via-white to-primary100'
      onClick={() => {}}
    >
      <PatientNavigatorSkipModal
        isOpen={skipModalOpen}
        onSkip={() => {
          trackEvent(TrackingEvents.PATIENT_NAVIGATOR_AGENT_CALL_END_CLICKED, {
            mostQualifiedTrialApplicationIdToken,
            trialApplicationIdToken: idToken,
          })
          endCall()
          advanceToNextStep()
        }}
        setIsOpen={setSkipModalOpen}
      />
      <button
        className='absolute right-4 top-4 z-navbarContainer flex size-8 items-center justify-center rounded-full bg-neutral200 text-neutral600 hover:bg-neutral300 hover:text-neutral800 md:right-14 md:size-14'
        onClick={() => {
          trackEvent(
            TrackingEvents.PATIENT_NAVIGATOR_AGENT_SKIP_BUTTON_CLICKED,
            {
              mostQualifiedTrialApplicationIdToken,
              trialApplicationIdToken: idToken,
              type: 'x button',
            },
          )
          setSkipModalOpen(true)
        }}
        type='button'
      >
        <Icon className='size-4' icon={faXmark} />
      </button>

      <div className='mx-4 flex h-full max-w-5xl flex-col gap-8 py-16 md:mx-auto md:pt-32'>
        <LiveAIAgent mediaRecorder={mediaRecorder} />

        <div className='flex h-full flex-col justify-between gap-4 md:flex-row md:items-start'>
          {mostRecentTranscript.length > 0 && (
            <TranscriptText mostRecentTranscript={mostRecentTranscript} />
          )}
          {mostRecentTranscript.length === 0 && (
            <div className='flex animate-pulse flex-col gap-4 md:w-2/3'>
              <div className='h-6 w-1/2 rounded bg-primary200'></div>
              <div className='h-6 w-4/5 rounded bg-neutral300'></div>
              <div className='h-6 w-4/5 rounded bg-neutral300'></div>
              <div className='h-6 w-4/5 rounded bg-neutral300'></div>
              <div className='h-6 w-4/5 rounded bg-neutral300'></div>
              <div className='h-6 w-full rounded'></div>
              <div className='h-6 w-1/2 rounded bg-primary200'></div>
            </div>
          )}
          <ApplicationCard patientName={getFirstName(patientName ?? '')} />
        </div>
      </div>
    </ModalOverlay>
  )
}

function ApplicationCard({ patientName }: { patientName?: string }) {
  return (
    <div className='fixed inset-x-0 bottom-0 flex flex-col items-center justify-center overflow-hidden rounded-2xl bg-white md:static md:w-80 md:drop-shadow-lg'>
      <div className='relative h-5 w-full'>
        <div className='absolute size-full bg-primary100'></div>
        <div className='absolute size-full w-[75%] bg-primary600'></div>
      </div>
      <div className='flex flex-col gap-6 p-4'>
        <div className='flex items-center gap-4'>
          <div className='flex w-2/3 items-center gap-3 rounded-md bg-primary50 px-4 py-2 text-sm font-medium text-primary600'>
            <Icon className='text-primary600' icon={faFileInvoice} size='2x' />{' '}
            {patientName ? `${patientName}'s` : 'Your'} Application
          </div>
          <div className='flex w-1/3 flex-col items-end gap-4'>
            <div className='flex items-center justify-end gap-2 text-xs font-bold'>
              75% done{' '}
              <Icon
                className='text-primary600'
                icon={faMemoCircleCheck}
                size='lg'
              />
            </div>
            <div className='flex items-center justify-end gap-2 text-xs font-bold'>
              3 items left{' '}
              <Icon
                className='text-primary200'
                icon={faMemoCircleInfo}
                size='lg'
              />
            </div>
          </div>
        </div>
        <div className='hidden w-full items-center justify-between gap-2 px-3 text-sm font-semibold md:flex'>
          Personal info
          <div className='flex items-center gap-2 text-primary600'>
            Verified
            <Icon className='text-primary600' icon={faCheckCircle} />
          </div>
        </div>
        <div className='hidden w-full items-center justify-between gap-2 px-3 text-sm font-semibold md:flex'>
          Conditions
          <div className='flex items-center gap-2 text-primary600'>
            Verified
            <Icon className='text-primary600' icon={faCheckCircle} />
          </div>
        </div>
        <div className='hidden w-full items-center justify-between gap-2 px-3 text-sm font-semibold md:flex'>
          Medication
          <div className='flex items-center gap-2 text-primary600'>
            Verified
            <Icon className='text-primary600' icon={faCheckCircle} />
          </div>
        </div>
        <div className='hidden w-full items-center justify-between gap-2 rounded-lg bg-primary50 px-3 py-2 text-sm font-semibold text-primary600 md:flex'>
          Verification
          <div className='flex items-center gap-4 text-primary600'>
            In-progress
            <Icon
              className='animate-spin text-primary600'
              icon={faSpinner}
              size='lg'
            />
          </div>
        </div>
      </div>
    </div>
  )
}

function TranscriptText({
  mostRecentTranscript,
}: {
  mostRecentTranscript: string
}) {
  const transcriptRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight
    }
  }, [mostRecentTranscript])

  return (
    <div className='h-48 overflow-scroll md:w-2/3' ref={transcriptRef}>
      <Text styleName='h3' value={mostRecentTranscript} />
    </div>
  )
}
